import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import { useQueryLoader } from 'react-relay';
import AddIcon from '@atlaskit/icon/core/migration/add';
import Select, { components, type MenuProps } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { defaultSelectStyles } from '@atlassian/jira-issue-field-select-base/src/ui/react-select-styles/styled.tsx';
import {
	FooterContainer,
	OptionLabel,
} from '@atlassian/jira-issue-internal-field-select/src/common/styled.tsx';
import createReleaseModalQuery, {
	type createReleaseModalQuery as createReleaseModalQueryType,
} from '@atlassian/jira-relay/src/__generated__/createReleaseModalQuery.graphql';
import type { Version } from '@atlassian/jira-software-releases-release-modals-relay/src/common/ui/types.tsx';
import { useCreateReleaseExperienceStart } from '@atlassian/jira-software-releases-release-modals-relay/src/services/create-release-experience-tracker/index.tsx';
import { CreateReleaseModal } from '@atlassian/jira-software-releases-release-modals-relay/src/ui/create-release-modal/async.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import type { VersionOption } from '../../common/types.tsx';
import messages from '../../messages.tsx';
import type { VersionsProps } from './types.tsx';
import { getGroupedOptions, removeArchivedOptions } from './utils.tsx';

const Versions = (props: VersionsProps) => {
	const {
		value,
		options,
		onChange,
		placeholder = '',
		isInvalid,
		fieldId,
		isMulti,
		autoFocus = false,
		isDisabled,
		onCloseMenuOnScroll,
		isDropdownMenuFixedAndLayered,
		inputId,
		isRequired,
		classNamePrefix,
		isClearable = true,
		ariaLabel,
		ariaLabelledBy,
		openMenuOnFocus = false,
		onBlur,
		shouldShowCreateVersion = false,
		projectId = '',
	} = props;
	const accountId = useAccountId();
	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
	const [createReleaseFormQueryRef, loadQuery] =
		useQueryLoader<createReleaseModalQueryType>(createReleaseModalQuery);
	const experienceStart = useCreateReleaseExperienceStart('version-edit-field');

	const { formatMessage } = useIntl();

	const getIsClearable = () => {
		if (!isClearable) {
			return false;
		}
		return Array.isArray(value) ? value.length > 0 : Boolean(value);
	};

	const loadCreateReleaseQuery = useCallback(() => {
		if (!createReleaseFormQueryRef && accountId) {
			loadQuery({ accountId });
		}
	}, [accountId, createReleaseFormQueryRef, loadQuery]);

	const onFooterClick = useCallback(() => {
		experienceStart();
		loadCreateReleaseQuery();
		setIsDialogOpen(true);
	}, [experienceStart, loadCreateReleaseQuery]);

	const onSuccess = useCallback(
		(version: Version) => {
			if (onChange && version.ari) {
				let currentValues: VersionsProps['value'] = [];
				if (value) {
					currentValues = Array.isArray(value) ? value : [value];
				}
				const newVersion: VersionOption = {
					label: version.name,
					value: version.id,
					status: 'UNRELEASED',
				};
				onChange(isMulti ? [...currentValues, newVersion] : newVersion);
			}
		},
		[isMulti, onChange, value],
	);

	const FooterMenu = useCallback(
		({ children, ...menuProps }: MenuProps<VersionOption, boolean>) => (
			<components.Menu {...menuProps}>
				{children}
				{shouldShowCreateVersion && (
					<CreateReleaseVersionFooter
						data-testid="issue-field-versions.ui.edit.version-footer"
						onClick={onFooterClick}
						onFocus={loadCreateReleaseQuery}
						onMouseOver={loadCreateReleaseQuery}
					>
						<AddIcon label={formatMessage(messages.createNewItem)} size="small" />
						<OptionLabel>{formatMessage(messages.createNewItem)}</OptionLabel>
					</CreateReleaseVersionFooter>
				)}
			</components.Menu>
		),
		[formatMessage, loadCreateReleaseQuery, onFooterClick, shouldShowCreateVersion],
	);

	return (
		<>
			{isDialogOpen && createReleaseFormQueryRef != null && (
				<CreateReleaseModal
					queryReference={createReleaseFormQueryRef}
					onClose={() => setIsDialogOpen(false)}
					projectId={projectId}
					onReleaseCreation={onSuccess}
				/>
			)}
			<Select
				placeholder={placeholder}
				options={getGroupedOptions(removeArchivedOptions(options), formatMessage)}
				// @ts-expect-error - TS2322 - Type '"error" | null' is not assignable to type 'ValidationState | undefined'.
				validationState={isInvalid === true ? 'error' : null}
				value={value}
				fieldId={fieldId}
				isMulti={isMulti}
				menuPosition={isDropdownMenuFixedAndLayered === true ? 'fixed' : undefined}
				// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides -- Ignored via go/DSP-18766
				styles={defaultSelectStyles}
				// @ts-expect-error - TS2322 - Type '(option: VersionOption | VersionOption[] | null) => void' is not assignable to type '(value: VersionOption | OptionsType<VersionOption> | null, action: ActionMeta<VersionOption>) => void'.
				onChange={onChange}
				autoFocus={autoFocus}
				isDisabled={isDisabled}
				closeMenuOnScroll={onCloseMenuOnScroll}
				isClearable={getIsClearable()}
				inputId={inputId}
				required={isRequired}
				classNamePrefix={classNamePrefix}
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledBy}
				openMenuOnFocus={openMenuOnFocus}
				{...(expVal('sunsetting_bulk_edit', 'isEnabled', false) && { onBlur })}
				{...(fg('jracloud-93591-create-new-version-gic') && {
					hasOtherFocusableElements: shouldShowCreateVersion,
					components: { Menu: FooterMenu },
				})}
			/>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const CreateReleaseVersionFooter = styled(FooterContainer)({
	borderTop: `2px solid ${token('color.border')}`,
	paddingTop: token('space.150'),
	paddingBottom: token('space.150'),
	cursor: 'default',
});

export default Versions;
